import { select } from '@angular-redux/store';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from "@angular/core";
import { Observable, of as observableOf, Subject, throwError } from 'rxjs';
import { catchError, filter, mergeMap, take, tap } from 'rxjs/operators';
import { Utils } from 'sharedFeatures/utils';

declare let handleAngularHttpError: any;

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

    customerResetCodeNotFoundText = $localize`error: customer reset code not found`;
    incorrectUserPassword = $localize`error: password incorrect`;
    treeGridTableIsNullText = $localize`TreeGrid table is null`;
    timeoutText = $localize`timeout`;
    tooManyUsersOnOrganisationText = $localize`too many users on the organisation`;
    tooManyPlansText = $localize`Too many plans`;
    loadObjectHelpFailedText = $localize`error: load object help failed`;
    userIsOnAnotherOrganistaionAlreadyText = $localize`user is on another organisation already`;
    checkPlanCredentialsFailedText = $localize`check plan credentials failed`;
    checkSubscriptionCredentialsFailedText = $localize`check subscription credentials failed`;
    accessDeniedText = $localize`Access denied`;
    planNotStartedText = $localize`Plan not started`;
    xeroText = $localize`Xero `;
    existingComponentText = $localize`Existing component`;
    cannotMoveComponentErrorText = $localize`Components that have an opening amount must always start at the start of the Timeline and cannot be moved to Groups that start after the start of the Timeline.`;
    cannotMoveComponentText = $localize`Cannot move component`;
    planLoadFailedText = $localize`plan load failed`;
    planLoadingFromDatabaseFailedText = $localize`plan loading from database failed`;
    planLoadingFromRedisFailedText = $localize`plan loading from Redis failed`;
    savePlanFailedText = $localize`save plan failed`;
    tourPlanExpiredText = $localize`Tour plan expired, page will be refreshed automatically`;
    tourPlanErrorText = $localize`Tour plan error`;
    planWasNotFoundText = $localize`Plan was not found`;
    xeroMethodNotSupportedText = $localize`Xero method not supported`;
    xeroTokenExpiredText = $localize`Xero token expired`;
    xeroRefreshDataStartErrorText = $localize`Xero refresh data start error`;
    userIsNotAuthenticatedText = $localize`user is not authenticated`;
    planNameIsEmptyText = $localize`plan name is empty`;
    reachedMaximumNumberOfPlansText = $localize`reached the maximum number of plans`;
    somethingUnexpectedHappenedText = $localize`Something unexpected happened. \n Try again and if it happens again contact support.`;
    oopsText = $localize`Oops!`;
    xeroUnknownErrorText = $localize`Xero unknown error`;
    public static xeroErrorMessageText = $localize`Sorry, we got into problems importing data out of Xero. Please, contact the support`;

    private appComponent: any = null;

    @select(['appData', 'appComponent'])
    readonly appComponent$!: Observable<any>;

    @select(['accountAppData', 'accountAppComponent'])
    readonly accountAppComponent$!: Observable<any>;

    idSrvUrl = window.idSrvUrl;

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        if (req.headers.get('skipInterceptor')) {
            const cleanReq = req.clone({
                headers: req.headers.delete('skipInterceptor') // Remove flag before sending
            });
            return next.handle(cleanReq);
        }

        let conversationId = window.localStorage.getItem('ConversationId');

        // Set new ConversationId
        if (!conversationId) {
            Utils.checkConversationId();
            conversationId = window.localStorage.getItem('ConversationId');
        }

        if (req.url.includes('/assets/lang/')) {
            return next.handle(req); // Bypass the interceptor for translation requests
        }

        // Clone origin request
        if (req.url !== (this.idSrvUrl + '/connect/token')) //interceptor overwrites Authorization header on token request
            req = req.clone({
                // Add ConversationId header
                setHeaders: {
                    'ConversationId': conversationId ? conversationId : '',
                    'Authorization': 'Bearer ' + localStorage.getItem("accessToken")
                },
                // Change responseType from json to text, because server can return plain text 'error=sessiontimeout' instead of json
                responseType: 'text'
            });

        if(req.url.includes(window.appPath)) {
            req = req.clone({
                setHeaders: {
                    'X-XSRF-Token' : (<HTMLInputElement>document.getElementsByName('__RequestVerificationToken')[0]).value
                }
            });
        }
        //console.log("intercepted", req);

        let errorMessage: string = '';
        let raiseError = false;
        if (!this.appComponent$ && !this.accountAppComponent$)
            return next.handle(req);
        return next.handle(req).pipe(
            tap(res => {
                if (!(res instanceof HttpResponse)) return; // Start of request

                errorMessage = this.getResponseErrorMessage(res);
                //console.log("intercepted 2", errorMessage);
                if (errorMessage === this.customerResetCodeNotFoundText) return;

                if (errorMessage) {
                    raiseError = true;

                    // Process error on Plan page
                    if (!window.isFromAccount) {
                        this.appComponent$.pipe(
                            filter(x => !!x),
                            take(1),)
                            .subscribe((appComponent: any) => {
                                this.appComponent = appComponent;

                                if (errorMessage.indexOf(this.treeGridTableIsNullText) !== -1) {
                                    // need to check balancer plan loaded

                                    // Call balancer method CheckPlanLoaded and repeat request
                                    raiseError = false;

                                    appComponent.getAppData().setReloadDashboard(true);
                                }
                                else if (errorMessage === this.timeoutText) {
                                    appComponent.showTimeout();
                                }
                                else if (errorMessage.indexOf(this.tooManyUsersOnOrganisationText) !== -1) {
                                    appComponent.showTeamIsFullDialog();
                                }
                                else if (errorMessage.indexOf(this.userIsOnAnotherOrganistaionAlreadyText) !== -1) {
                                    appComponent.showUserAlreadyOnTeamDialog();
                                }
                                else if (errorMessage === this.loadObjectHelpFailedText) {
                                    // do nothing
                                }
                                else if (errorMessage.indexOf(appComponent.getAppData().overLimitObjectMsg) !== -1) {
                                    raiseError = false;
                                    appComponent.showCounterWarningWin();
                                }
                                else {
                                    raiseError = this.processCustomError(req.url, appComponent, errorMessage);
                                }
                            });
                    }
                    // Process error on AccountSettings page
                    else {
                        this.accountAppComponent$.pipe(
                            filter(x => !!x),
                            take(1),)
                            .subscribe((accountAppComponent: any) => {
                                if (errorMessage === this.timeoutText) {
                                    // do nothing
                                }
                                else if (errorMessage.indexOf(this.tooManyUsersOnOrganisationText) !== -1) {
                                    accountAppComponent.showTeamIsFullDialog();
                                }
                                else if (errorMessage.indexOf(this.userIsOnAnotherOrganistaionAlreadyText) !== -1) {
                                    accountAppComponent.showUserAlreadyOnTeamDialog();
                                }
                                else if (errorMessage === this.loadObjectHelpFailedText) {
                                    // do nothing
                                }
                                else if (errorMessage.indexOf(this.tooManyPlansText) !== -1) {
                                    accountAppComponent.showTooManyPlansDialog();
                                }
                                else {
                                    raiseError = this.processCustomError(req.url, accountAppComponent, errorMessage);
                                }
                            });
                    }
                }
            }),
            mergeMap(res => {
                // We have to raise error to stop Observable chain
                if (raiseError)
                    return throwError(() => new Error('errorHandled'));

                // If balancer has unloaded plan then load plan and repeat request
                if (errorMessage.indexOf(this.treeGridTableIsNullText) !== -1 && this.appComponent) {
                    return observableOf(null).pipe(
                        mergeMap(() => this.appComponent.checkPlanLoaded()),
                        mergeMap(x => {
                            // Repeat request
                            return next.handle(req).pipe(
                                tap(res => {
                                    if (!(res instanceof HttpResponse)) return; // Start of request

                                    errorMessage = this.getResponseErrorMessage(res);

                                    // If error was repeated show error dialog
                                    if (errorMessage.indexOf(this.treeGridTableIsNullText) !== -1) {
                                        this.appComponent.showPlanNotLoadedError();

                                        this.appComponent.getAppData().setReloadDashboard(true);

                                        return throwError(() => new Error('errorHandled'));
                                    }
                                }));
                        }),);
                }
                else {
                    return observableOf(res);
                }
            }),
            catchError(this.handleError),);
    }

    private getResponseErrorMessage(res: HttpResponse<any>): string {
        let errorMessage: string = '';

        if (res.body && res.body.length) {
            if (res.body.indexOf("error=sessiontimeout") != -1) {
                errorMessage = this.timeoutText;
            }
            else if (res.body.indexOf("error=CheckUserPlanCredentialsFailed") != -1) {
                errorMessage = this.checkPlanCredentialsFailedText;
            }
            else if (res.body.indexOf("error=CheckUserSubscriptionCredentialsFailed") != -1) {
                errorMessage = this.checkSubscriptionCredentialsFailedText;
            }
            else {
                try {
                    const data = JSON.parse(res.body);

                    if (!data.success && data.errorMessage)
                        errorMessage = data.errorMessage;
                }
                catch (ex) {
                }
            }
        }

        return errorMessage;
    }

    handleError(errorResponse: any) {
        let errMsg: string;
        if (errorResponse instanceof HttpErrorResponse) {
            const err = errorResponse.message || JSON.stringify(errorResponse.error);
            errMsg = `${errorResponse.status} - ${errorResponse.statusText || ''} Details: ${err}`;
        } else {
            errMsg = errorResponse.message ? errorResponse.message : errorResponse.toString();
        }

        if (errMsg.indexOf('errorHandled') !== -1 || (errorResponse.status == 0 && (window.unloadingState || errorResponse.error?.type == 'abort'))) {
            return new Subject<HttpResponse<any>>();
        }

        console.error('http.request error', errorResponse);

        errorResponse.statusText = errMsg;
        handleAngularHttpError(errorResponse);

        // Return empty Observable without raising error
        return new Subject<HttpResponse<any>>();
        //return observableThrowError(errorResponse);
    }

    // Do individual actions for some errors
    private processCustomError(url: string, appComponent: any, errorMessage: string): boolean {
        let callback: Function | undefined;

        // Find server controller method name
        if (url && url.length) {
            // AuthController.CheckIsLoggedIn
            if (url.indexOf('CheckIsLoggedIn') !== -1) {
                // Access denied for shared plan
                if (errorMessage.indexOf(this.accessDeniedText) !== -1) {
                    appComponent.showAccessDeniedDialog(() => {
                        appComponent.getRedirectService().toAccount();
                    });

                    return true;
                }
                else if (errorMessage.indexOf(this.planNotStartedText) !== -1) {
                    // last saved plan is deleted
                    appComponent.getRedirectService().toAccount();
                }
                else if (errorMessage.indexOf(this.xeroText) !== -1) {
                    // Xero newplan/import errors
                    appComponent.getRedirectService().toAccount();
                }
                else {
                    appComponent.showPlanLoadingError();
                    return true;
                }
            }
            // TreeGrid/MoveNode (MoveNodeUpDown, MoveNodes)
            else if (url.indexOf('MoveNode') !== -1) {
                // Moving existing components to groups starting later in the plan
                if (errorMessage.indexOf(this.existingComponentText) !== -1) {
                    errorMessage = this.cannotMoveComponentErrorText;

                    appComponent.showErrorDialog(errorMessage, null, {
                        title: this.cannotMoveComponentText,
                        height: 230
                    });

                    return true;
                }
            }
            else if ((errorMessage.indexOf(this.planLoadFailedText) !== -1)
                || (errorMessage.indexOf(this.planLoadingFromDatabaseFailedText) !== -1)
                || (errorMessage.indexOf(this.planLoadingFromRedisFailedText) !== -1)
                || (errorMessage.indexOf(this.savePlanFailedText) !== -1)) {

                let isTour = false;

                if (appComponent.planData)
                    isTour = appComponent.planData.planTourActive;

                if (isTour) {
                    // BR-4967, BR-4989 tour exclusive error message & auto-refresh page
                    errorMessage = this.tourPlanExpiredText;

                    appComponent.setCheckForUnsavedChanges(false);
                    appComponent.showErrorDialog(errorMessage, () => {
                        this.appComponent.getPlanService().resetDemoTour();
                        document.location.reload();
                    }, {
                        title: this.tourPlanErrorText,
                        height: 230
                    });

                }
                else {
                    appComponent.showErrorDialog(errorMessage, callback);
                    return true;
                }

            }
            else if (errorMessage.indexOf(this.planWasNotFoundText) !== -1) {
                callback = () => {
                    appComponent.getRedirectService().toAccount();
                };
            }
            else if (url.indexOf('UpdatePassword') !== -1) {
                return false;
            }
            else if (errorMessage.indexOf('password incorrect') !== -1) {
                return false;
            }
            else if ((errorMessage.indexOf(this.xeroMethodNotSupportedText) !== -1)
                || (errorMessage.indexOf(this.xeroTokenExpiredText) !== -1)) {
                appComponent.showErrorDialog(errorMessage, callback);
                return false;
            }
            else if (errorMessage.indexOf(this.xeroRefreshDataStartErrorText) !== -1) {
                appComponent.showErrorDialog(errorMessage, callback);
                return false;
            }
            else if (errorMessage.indexOf(this.userIsNotAuthenticatedText) !== -1) {
                callback = () => {
                    appComponent.getRedirectService().openLandingSignIn();
                };
            }
            else if (errorMessage.indexOf(this.planNameIsEmptyText) != -1) {
                return false;
            }
            else if (errorMessage.indexOf(this.reachedMaximumNumberOfPlansText) !== -1) {
                appComponent.showErrorDialog(errorMessage, callback);
                return true;
            }
            else if (errorMessage.indexOf(this.xeroUnknownErrorText) !== -1) {                 
                appComponent.showErrorDialog(ErrorInterceptor.xeroErrorMessageText, () => {
                    document.location.reload();
                }, { keepParent: true, isModal: true }, true);
                return true;
            }
        }

        errorMessage = this.somethingUnexpectedHappenedText;

        appComponent.showErrorDialog(errorMessage, callback, { title: this.oopsText });
        return true;
    }
}
